import { InfoTag } from "@components/library";
import { COLORS, FEATURE_FLAGS } from "@constants";
import { RootState } from "@redux/store";
import {
  company_proposal_library_path,
  company_scientist_network_path,
  discover_path,
  inbox_path,
  innovators_path,
  my_proposals_path,
  proposal_review_system_path,
  reviewer_dashboard_path,
  reviewer_dashboard_teams_path,
  university_dashboard_proposals_path,
  university_dashboard_team_path,
} from "@routes/routes";
import { marketplacePath } from "@routes/utils";
import { UserRole } from "@tsTypes/users";
import { assertAbsurd } from "@utils/tsUtils";
import { ReactNode } from "react";
import { useSelector } from "react-redux";

interface INavbarLink {
  label: ReactNode;
  to: string;
  dropdownItems?: {
    label: ReactNode;
    to: string;
  }[];
  withNewTag?: boolean;
  asAnchor?: boolean;
  activeStylesMatchType?: "startsWith" | "exact";
  "data-testid"?: string;
}

const useNavbarLinks = (): INavbarLink[] => {
  const currentUser = useSelector((state: RootState) => state.profiles.currentUser);
  const unreadCount = useSelector((state: RootState) => state.profiles.unreadCount);
  const subscription = useSelector((state: RootState) => state.company.subscription);

  const marketplaceLink = {
    label: "Marketplace",
    to: marketplacePath(),
  };

  const inboxLink = {
    label: (
      <>
        Inbox
        {unreadCount > 0 && (
          <InfoTag marginLeft="4px" backgroundColor={COLORS.RED_700}>
            {unreadCount}
          </InfoTag>
        )}
      </>
    ),
    to: inbox_path(),
  };

  switch (currentUser.role) {
    // User is logged out
    case undefined: {
      return [
        {
          ...marketplaceLink,
          activeStylesMatchType: "startsWith",
          asAnchor: true,
        },
        {
          label: "For Innovators",
          to: innovators_path(),
          asAnchor: true,
        },
        {
          label: "Blog",
          to: "https://blog.halo.science/",
          asAnchor: true,
        },
      ];
    }
    case UserRole.SCIENTIST: {
      const links: INavbarLink[] = [
        marketplaceLink,
        {
          label: "My proposals",
          to: my_proposals_path(),
        },
      ];
      if (currentUser.profile_info.is_expert) {
        links.push({
          label: "Assigned reviews",
          to: proposal_review_system_path(),
        });
      }
      links.push(inboxLink);

      return links;
    }
    case UserRole.SPONSOR: {
      if (!currentUser.profile_info.verified && !currentUser.profile_info.is_admin) {
        return [];
      }

      const links: INavbarLink[] = [];
      if (FEATURE_FLAGS.DISCOVER_FEED) {
        links.push({
          label: "Discover",
          to: discover_path(),
          withNewTag: true,
          activeStylesMatchType: "startsWith",
        });
      }
      if (!currentUser.profile_info.commenter) {
        links.push({
          label: "Partnering Requests",
          to: reviewer_dashboard_path(),
        });
      }
      if (subscription?.features.proposal_library) {
        links.push({
          label: "Proposal Library",
          to: company_proposal_library_path(),
        });
      }
      if (subscription?.features.company_scientist_network && !FEATURE_FLAGS.DISCOVER_FEED) {
        links.push({
          label: "Network",
          to: company_scientist_network_path(),
          withNewTag: true,
          activeStylesMatchType: "startsWith",
        });
      }
      if (currentUser.profile_info.verified) {
        links.push({
          label: "My Team",
          to: reviewer_dashboard_teams_path(),
        });
      }
      if (currentUser.profile_info.verified && !currentUser.profile_info.commenter) {
        links.push({
          ...inboxLink,
          "data-testid": "navbar-inbox-link",
        });
      }

      return links;
    }
    case UserRole.UNIVERSITY_ADMIN: {
      return [
        marketplaceLink,
        {
          label: "Proposals",
          to: university_dashboard_proposals_path(),
        },
        {
          label: "My Team",
          to: university_dashboard_team_path(),
        },
        inboxLink,
      ];
    }
    default: {
      assertAbsurd(currentUser.role as never);
    }
  }
};

export default useNavbarLinks;
